export const PREPUBLISH_RENDER_PIPELINE_VERSION = 1;
export const PUBLISH_PRERENDER_WATERMARK_IDENTITY = "scroll-watermark-v1";

export const ACTIVE_PUBLISH_PRERENDER_STATUSES = [
  "uploading",
  "queued",
  "processing",
  "ready",
] as const;

export type ActivePublishPrerenderStatus = (typeof ACTIVE_PUBLISH_PRERENDER_STATUSES)[number];
export type PublishPrerenderStatus =
  | ActivePublishPrerenderStatus
  | "failed"
  | "superseded"
  | "expired"
  | "promoted";

export interface PublishPrerenderKeyInput {
  pipelineVersion: number;
  timelineProject: unknown;
  linkOverlays: unknown;
  voiceover: unknown;
  applyWatermark: boolean;
  watermarkIdentity: string;
}

export function isActivePublishPrerenderStatus(
  status: string | null | undefined,
): status is ActivePublishPrerenderStatus {
  return ACTIVE_PUBLISH_PRERENDER_STATUSES.includes(status as ActivePublishPrerenderStatus);
}

export function buildPublishPrerenderKey(input: PublishPrerenderKeyInput): string {
  return stableHash(stableStringify(input));
}

export function stableStringify(value: unknown): string {
  if (value == null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, entryValue]) => entryValue !== undefined)
    .sort(([a], [b]) => a.localeCompare(b));
  return `{${entries.map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringify(entryValue)}`).join(",")}}`;
}

export function stableHash(input: string): string {
  let h1 = 0xdeadbeef ^ input.length;
  let h2 = 0x41c6ce57 ^ input.length;
  for (let i = 0; i < input.length; i += 1) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return `${(h2 >>> 0).toString(16).padStart(8, "0")}${(h1 >>> 0).toString(16).padStart(8, "0")}`;
}
